import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast, SimpleTable, Input, Button } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { fetchPromoCodeUsages, deletePromoCodeUsage } from '../../../api/PromoCodeUsageAdminApi';
import { createPromoCodeUsageColumns } from './tableColumns';
import styles from './PromoCodeUsages.module.scss';
const DEFAULT_FILTERS = {
    created_at_from: '',
    created_at_to: '',
};
const PromoCodeUsages = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [promoCodeUsages, setPromoCodeUsages] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [isRunningBulkAction, setIsRunningBulkAction] = useState(false);
    const latestRequestIdRef = useRef(0);
    const loadPromoCodeUsages = useCallback(async (search, activeFilters = DEFAULT_FILTERS) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const response = await fetchPromoCodeUsages({
                search: search || undefined,
                created_at_from: activeFilters.created_at_from || undefined,
                created_at_to: activeFilters.created_at_to || undefined,
            });
            // If a newer request has been started, ignore this response.
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            const data = response.body;
            const usageArray = Array.isArray(data) ? data : data.results || [];
            const transformedUsages = usageArray.map((usage) => ({
                id: usage.id,
                user: usage.user,
                clinic: usage.clinic,
                promo_code: usage.promoCode ?? usage.promo_code,
                order: usage.order,
                discount_applied: usage.discountApplied ?? usage.discount_applied,
                created_at: usage.createdAt ?? usage.created_at,
                created_at_display: usage.createdAtDisplay ?? usage.created_at_display,
            }));
            setPromoCodeUsages(transformedUsages);
        }
        catch (error) {
            // If a newer request has been started, ignore handling this error state.
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            console.error('Error loading promo code usages:', error);
            showToast({ message: 'Failed to load promo code usages', variant: 'error', noBorder: true });
            setPromoCodeUsages([]);
        }
        finally {
            // Only clear the loading state if this is still the latest request.
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [showToast]);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadPromoCodeUsages(searchTerm, appliedFilters);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadPromoCodeUsages, searchTerm, appliedFilters]);
    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };
    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };
    const handleResetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
    };
    const handleBulkDelete = async (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIdList = selectedRows.map((row) => row.id);
        if (!window.confirm(`Are you sure you want to delete ${selectedIdList.length} usage record${selectedIdList.length === 1 ? '' : 's'}?`))
            return;
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIdList.map((id) => deletePromoCodeUsage(id)));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIdList[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                setPromoCodeUsages((prev) => prev.filter((u) => !successfulIds.includes(u.id)));
                const messageCount = successfulIds.length;
                showToast({
                    message: `Deleted ${messageCount} usage record${messageCount === 1 ? '' : 's'}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failedIds.length) {
                console.error('Error deleting usage records for IDs:', failedIds);
                const failedCount = failedIds.length;
                showToast({
                    message: `Failed to delete ${failedCount} usage record${failedCount === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error deleting usage records:', error);
            showToast({ message: 'Failed to delete selected records', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const columns = createPromoCodeUsageColumns();
    const bulkActions = [
        {
            label: 'Delete',
            action: (selectedRows) => handleBulkDelete(selectedRows),
        },
    ];
    return (_jsxs(AdminLayout, { children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "created-at-from", children: "Created From" }), _jsx("input", { id: "created-at-from", type: "date", value: filters.created_at_from, onChange: (event) => handleFilterChange('created_at_from', event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "created-at-to", children: "Created To" }), _jsx("input", { id: "created-at-to", type: "date", value: filters.created_at_to, onChange: (event) => handleFilterChange('created_at_to', event.target.value) })] }), _jsxs("div", { className: styles.filterActions, children: [_jsx(Button, { variant: "primaryV2", size: "small", label: "Apply Filters", onClick: handleApplyFilters }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Reset", onClick: handleResetFilters })] }), _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by user, email, promo code, or order number", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) })] }), _jsx(SimpleTable, { columns: columns, data: promoCodeUsages, rowHeight: "60px", tableHeading: "Promo Code Usages", titleStyle: {
                            color: '#000',
                            fontFamily: 'Nunito',
                            fontSize: '24px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '28px'
                        }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Usages per page", pageSize: 20, bulkActions: bulkActions })] })] }));
};
export default PromoCodeUsages;
